/**
 * ./exercise8/Exercise8Warmup.js
 *
 * This module helps users improve **speed reading** using a **progressive phrase flashing technique**.
 *
 * **Updated Features:**
 * -------------------------------------
 * - Automatically selects a random article at the start.
 * - Starts directly in the Workout mode (No article selection step).
 * - Words flash progressively.
 * - Auto-Redirect to a Comprehension Test.
 */

import React, { useState, useEffect } from "react";
import "./Exercise8Workout.css"; // Import the CSS file
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "../components/icons/close.svg";
import InfoIcon from "../components/icons/InfoIcon.svg";
import PlayButton from "../components/icons/play.svg";
import { updateUserProgress } from "../utility";

const Exercise8Warmup = () => {
    const navigate = useNavigate();
    const TIMER_DURATION = 1.5 * 60; // 10 minutes in seconds
    const location = useLocation();
    const isWarmup = location.state?.isWarmup;
    const exerciseNumber = location.state?.exerciseNumber;
  
    // Automatically select a random article at the start
    const [text, setText] = useState("Loading...");
    const [articleName, setArticleName] = useState("Article 1");
  
    const [progress, setProgress] = useState(0);
    const [phrasesIndex, setPhrasesIndex] = useState(0);
    const [paragraphIndex, setParagraphIndex] = useState(0);
    const [isFlashing, setIsFlashing] = useState(false);
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [levelSelection, setLevelSelection] = useState(2); // Default Level 2 words per phrase
    const [exerciseStep, setExerciseStep] = useState("Workout");

    useEffect(() => {
        fetch("../Exercise8Article/Article1.txt") // Path from `public/`
          .then((response) => response.text())
          .then((data) => setText(data))
          .catch((error) => {
            console.error("Error loading article:", error);
            setText("Failed to load article.");
          });
      }, []);
    
      const phraseSplitter = () => {
        if (!text || text === "Loading..." || text === "Failed to load article.") return [[]];
        const words = text.split(/\s+/); // Split into individual words
        const paragraphs = [];
        let paragraphPhrases = [];
    
        for (let i = 0; i < words.length; i += levelSelection) {
          paragraphPhrases.push(words.slice(i, i + levelSelection).join(" "));
        }
    
        paragraphs.push(paragraphPhrases);
        return paragraphs;
      };

  let paragraphs = phraseSplitter(text);

  useEffect(() => {
    if (isFlashing && timeElapsed < TIMER_DURATION) {
        const intervalId = setInterval(() => {
          setPhrasesIndex((prevIndex) => prevIndex + 1);
          setTimeElapsed((prevTime) => {
            const newTime = prevTime + 0.3;
            setProgress((newTime / TIMER_DURATION) * 100);
            return newTime;
          });
  
          if (phrasesIndex === paragraphs[paragraphIndex]?.length - 1) {
            setPhrasesIndex(0);
            setParagraphIndex((prevIndex) => prevIndex + 1);
          }
        }, 300);
  
        return () => clearInterval(intervalId);
      }

      if (timeElapsed >= TIMER_DURATION) {
        setIsFlashing(false);
        const timeoutId = setTimeout(() => {
          if (isWarmup) {
              updateUserProgress(exerciseNumber, -1, 0);
              navigate("/Warmupfinish", {
                state: {
                  exerciseNumber: exerciseNumber,
                  returnTo: location.state?.returnTo,
                  isWarmup: true 
                }
              });
  
          } else {
              navigate("/exercise7test", {
                state: {
                  articleText: text,
                  articleName: articleName,
                }
              });
          }
  
        }, 500);
    
        return () => clearTimeout(timeoutId);
      }
    }, [isFlashing, phrasesIndex, paragraphs.length, timeElapsed, navigate, text, articleName]);

  const handleClose = () => {
    navigate("/");
  };

  const playWordFlasher = () => {
    setIsFlashing(true);
  };

  const renderExercise = () => {
    if (!text || text === "No content available") {
        return <p>Loading article...</p>; // Show fallback if text is empty
      }
    
    if (exerciseStep == "Workout") {
      return (
        <div>
          {paragraphs.map((paragraph, paragraphsIndex) => (
            <div className="article-reader">
              {paragraph.map((word, wordIndex) => (
                <div className="phrase">
                  {word}
                  <div
                    className={`text-cover ${
                      isFlashing &&
                      wordIndex == phrasesIndex &&
                      paragraphsIndex == paragraphIndex
                        ? "display-none"
                        : ""
                    }`}
                  ></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }
  };


  return (
    <div className="exercise-container">
      <div className="header-bar">
        <div className="close-icon-wrapper">
          <img
            src={CloseIcon}
            alt="close icon"
            className="close-icon"
            onClick={handleClose}
          />
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div className="progress" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        <div className="close-icon-wrapper">
          <img
            src={PlayButton}
            alt="play button"
            onClick={playWordFlasher}
            width="50px"
          />
        </div>
      </div>
      
      <div className="main-content-8">{renderExercise()}</div>

      <div className="instruction-box">
        <img src={InfoIcon} alt="info icon" className="instruction-icon" />
        <p>Focus on the flashing words.</p>
      </div>
    </div>
  );
};
export default Exercise8Warmup;
